import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import type { ChatMessage as ChatMessageType } from '@/types';

interface ChatMessageProps {
  message: ChatMessageType;
}

const AGENT_COLORS: Record<string, string> = {
  'Main Agent':     '#6366f1',
  'Analysis Agent': '#10b981',
  'Map Agent':      '#f59e0b',
};

// Detect ASCII chart blocks: no language tag + contains block-drawing chars
const isAsciiChart = (code: string, lang?: string): boolean => {
  if (lang) return false;
  return /[█▁▂▃▄▅▆▇┤┼╭─│╯┐└]/.test(code);
};

// Palette: 8-step gradient from cool blue → teal → green → amber
const BAR_PALETTE = [
  '#38bdf8', // sky-400
  '#22d3ee', // cyan-400
  '#2dd4bf', // teal-400
  '#34d399', // emerald-400
  '#a3e635', // lime-400
  '#facc15', // yellow-400
  '#fb923c', // orange-400
  '#f87171', // red-400
];

const BLOCK_CHARS = '▁▂▃▄▅▆▇█';

// Render a chart string as React nodes with colored block characters
const colorizeChart = (content: string, baseFontSize = '0.72rem'): React.ReactNode => {
  // Count total block chars to assign gradient position
  const allBlocks = content.split('').filter(c => BLOCK_CHARS.includes(c));
  const total = allBlocks.length || 1;
  let blockIdx = 0;

  return content.split('\n').map((line, li) => (
    <span key={li} style={{ display: 'block' }}>
      {line.split('').map((ch, ci) => {
        if (BLOCK_CHARS.includes(ch)) {
          const t = blockIdx++ / total;
          const palIdx = Math.min(Math.floor(t * BAR_PALETTE.length), BAR_PALETTE.length - 1);
          return <span key={ci} style={{ color: BAR_PALETTE[palIdx], fontSize: baseFontSize }}>{ch}</span>;
        }
        // Axis / structural chars — subtle highlight
        if (/[┤┼╭─│╯┐└╰]/.test(ch)) {
          return <span key={ci} style={{ color: '#475569', fontSize: baseFontSize }}>{ch}</span>;
        }
        return <span key={ci} style={{ fontSize: baseFontSize }}>{ch}</span>;
      })}
    </span>
  ));
};

interface LightboxProps {
  content: string;
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ content, onClose }) => (
  <div style={lbStyles.overlay} onClick={onClose}>
    <div style={lbStyles.box} onClick={e => e.stopPropagation()}>
      <button style={lbStyles.close} onClick={onClose} aria-label="Close">✕</button>
      <pre style={lbStyles.pre}>{colorizeChart(content, '1rem')}</pre>
    </div>
  </div>
);

const lbStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.65)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  box: {
    position: 'relative',
    background: '#0f172a',
    borderRadius: '0.75rem',
    padding: '2rem 2.5rem',
    maxWidth: '90vw',
    maxHeight: '85vh',
    overflow: 'auto',
    boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
  },
  close: {
    position: 'absolute',
    top: 10,
    right: 14,
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    fontSize: '1.1rem',
    cursor: 'pointer',
    lineHeight: 1,
  },
  pre: {
    margin: 0,
    fontFamily: '"Fira Code", "Cascadia Code", monospace',
    fontSize: '1rem',
    lineHeight: 1.7,
    color: '#94a3b8',
    whiteSpace: 'pre',
  },
};

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const agentColor = message.agent ? (AGENT_COLORS[message.agent] ?? '#6366f1') : undefined;
  const [lightboxContent, setLightboxContent] = useState<string | null>(null);

  // Custom renderer for code blocks
  const components = {
    code({ className, children, ...props }: React.ComponentPropsWithoutRef<'code'> & { inline?: boolean }) {
      const lang = className?.replace('language-', '');
      const content = String(children).replace(/\n$/, '');
      const isBlock = !props.inline;

      if (isBlock && isAsciiChart(content, lang)) {
        return (
          <div
            style={styles.chartWrapper}
            onClick={() => setLightboxContent(content)}
            title="Click to enlarge"
          >
            <pre style={styles.chartPre}>{colorizeChart(content, '0.72rem')}</pre>
            <span style={styles.chartHint}>🔍 click to enlarge</span>
          </div>
        );
      }
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
  };

  return (
    <>
      {lightboxContent && (
        <Lightbox content={lightboxContent} onClose={() => setLightboxContent(null)} />
      )}
      <div style={{
        ...styles.container,
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}>
        <div style={{ maxWidth: isUser ? '70%' : '80%' }}>
          {!isUser && message.agent && (
            <div style={{ ...styles.agentBadge, color: agentColor, borderColor: agentColor }}>
              <span style={{ ...styles.agentDot, backgroundColor: agentColor }} />
              {message.agent}
            </div>
          )}
          {isUser && (
            <div style={styles.userLabel}>You</div>
          )}
          <div style={{
            ...styles.message,
            backgroundColor: isUser ? '#3b82f6' : '#f1f5f9',
            color: isUser ? 'white' : '#0f172a',
            borderTop: !isUser && message.agent ? `2px solid ${agentColor}` : undefined,
          }}>
            {isUser ? (
              <p style={{ margin: 0 }}>{message.content}</p>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight, rehypeRaw]}
                components={components}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    marginBottom: '1rem',
  },
  agentBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    marginBottom: 3,
    paddingLeft: 2,
    border: '1px solid',
    borderRadius: '999px',
    padding: '1px 8px 1px 5px',
    width: 'fit-content',
    background: 'white',
  },
  agentDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  userLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#94a3b8',
    textAlign: 'right' as const,
    marginBottom: 3,
    paddingRight: 2,
  },
  message: {
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    lineHeight: '1.5',
    wordWrap: 'break-word',
  },
  chartWrapper: {
    position: 'relative',
    cursor: 'zoom-in',
    background: '#1e293b',
    borderRadius: '0.4rem',
    padding: '0.75rem 1rem',
    margin: '0.5rem 0',
    overflow: 'hidden',
    maxHeight: 160,
    userSelect: 'none',
  },
  chartPre: {
    margin: 0,
    fontFamily: '"Fira Code", "Cascadia Code", monospace',
    fontSize: '0.72rem',
    lineHeight: 1.6,
    color: '#94a3b8',
    whiteSpace: 'pre',
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  chartHint: {
    position: 'absolute',
    bottom: 6,
    right: 10,
    fontSize: '0.65rem',
    color: '#64748b',
    pointerEvents: 'none',
  },
};
